import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import type { AuthorizingProfessionalInfo, PhysicianInfo } from '@/types/RequisitionFormTypes'
import { toProperCaseName } from '@/utils/freeTextValidation'
import * as Sentry from '@sentry/react'
import type { Practitioner } from 'fhir/r4'

/**
 * Max number of practitioners the UI requests from POST /fhir/practitioner/search.
 *
 * The server accepts `count` in the range 1–100 (values outside the range, or non-integers,
 * are rejected with HTTP 422 before any Epic call). We cap the UI at 10 to keep the result
 * list short and the dropdown manageable.
 */
export const PRACTITIONER_SEARCH_COUNT = 10

/** Request body for POST /fhir/practitioner/search (proxied to Epic). */
export interface PractitionerSearchRequest {
  accessToken: string
  organizationId: string
  name?: string | null
  /**
   * Max number of practitioners to return. Integer in range 1–100; omit for Epic's default
   * page size. Only caps the number of array elements returned, not their structure.
   */
  count?: number | null
}

/** Organization/provider context from search response. */
export interface PractitionerSearchProviderInfo {
  id: string
  name?: string | null
  state?: string | null
}

/** Single practitioner from search response (medicalProfessionalInfo shape). */
export interface PractitionerSearchMedicalProfessional {
  npi?: string | null
  name?: string | null
  email?: string | null
  firstName?: string | null
  lastName?: string | null
  roleTitle?: string | null
  phone?: string | null
  /**
   * Two-letter US state for the practitioner, from the NPPES NPI Registry (practice LOCATION
   * state, falling back to MAILING). Distinct from any Epic/FHIR address. `null`/absent when the
   * result has no NPI, the NPI is not found in NPPES, or the lookup fails/times out — treat as
   * "state unknown", not an error. Best-effort: an NPPES outage still yields a 200 search with
   * `state: null` for affected rows.
   */
  state?: string | null
}

/** One practitioner in the search response. */
export interface PractitionerSearchResponseItem {
  providerInfo: PractitionerSearchProviderInfo
  medicalProfessionalInfo: PractitionerSearchMedicalProfessional
  fhirId: string
}

/**
 * Map a search response item into the authorizing provider shape expected by the UI.
 *
 * `roleTitle` is the provider's credential/title (e.g. "MD", "DO"), resolved server-side
 * from the org's `providerCredentialSystemId` config against the FHIR
 * `Practitioner.qualification`. It is config-driven free-text (not an enum) and is
 * frequently absent — normalized here to `''` so downstream (`PhysicianInfo.roleTitle: string`)
 * and display code can treat missing and empty identically.
 */
function mapSearchItemToAuthorizing(item: PractitionerSearchResponseItem): {
  authorizingProfessionalInfo: AuthorizingProfessionalInfo
  patientPhysician: PhysicianInfo
} {
  const m = item.medicalProfessionalInfo
  const firstName = toProperCaseName(m.firstName ?? '')
  const lastName = toProperCaseName(m.lastName ?? ((m.name ?? '').trim() || ''))
  const displayName =
    toProperCaseName(m.name ?? '') || [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown'
  const id = item.fhirId

  return {
    authorizingProfessionalInfo: {
      npi: m.npi ?? '',
      name: displayName,
      email: m.email ?? undefined,
    },
    patientPhysician: {
      id,
      name: { firstName, lastName },
      roleTitle: m.roleTitle?.trim() ?? '',
      phone: m.phone?.trim() ?? '',
      email: m.email ?? '',
      npi: m.npi ?? '',
      // Wire field is `medicalProfessionalInfo.state` (NPPES-derived); surfaced internally as
      // `npiState` to make the provenance explicit and avoid confusion with an address state.
      // Normalize empty/whitespace to undefined so the UI treats "unknown" (null/absent) uniformly.
      npiState: m.state?.trim() || undefined,
    },
  }
}

/**
 * Resolve the provider's credential/title (e.g. "MD", "DO") from a FHIR Practitioner using the
 * org-configured credential identifier system OID. Ported from the backend
 * ``role_title_from_oid`` (app/fhir/practitioner_parsing.py) so the read path matches the search
 * path and the config the UI receives.
 *
 * Precedence:
 *   1. OID configured + a qualification coding matches → that coding's ``display``, else that
 *      qualification's ``code.text``.
 *   2. OID configured + no coding matches → ``null`` (do not guess).
 *   3. OID not configured → the first qualification's ``code.text`` (legacy behavior).
 */
export function roleTitleFromOid(practitioner: Practitioner, credentialSystemId?: string | null): string | null {
  const qualifications = practitioner.qualification ?? []
  const target = credentialSystemId?.trim()
  if (!target) {
    for (const qual of qualifications) {
      const text = qual.code?.text?.trim()
      if (text) return text
    }
    return null
  }
  for (const qual of qualifications) {
    const code = qual.code
    for (const coding of code?.coding ?? []) {
      if ((coding.system ?? '').trim() === target) {
        return coding.display?.trim() || code?.text?.trim() || null
      }
    }
  }
  return null
}

/**
 * Map a FHIR R4 Practitioner resource into the authorizing provider shape expected by the UI.
 * NPI is extracted from an identifier with system 'http://hl7.org/fhir/sid/us-npi'.
 * Email is extracted from the first telecom entry with system 'email'.
 * roleTitle is resolved via {@link roleTitleFromOid} using the org's configured credential
 * identifier system OID (matching POST /fhir/practitioner/search), falling back to the first
 * qualification's ``code.text`` when the OID is not configured.
 */
function mapFhirPractitionerToAuthorizing(
  practitioner: Practitioner,
  fallbackId: string,
  credentialSystemId?: string | null
): {
  authorizingProfessionalInfo: AuthorizingProfessionalInfo
  patientPhysician: PhysicianInfo
} {
  const nameEl = practitioner.name?.[0]
  const firstName = toProperCaseName(nameEl?.given?.[0] ?? '')
  const lastName = toProperCaseName(nameEl?.family ?? '')
  const displayName =
    toProperCaseName(nameEl?.text ?? '') || [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown'
  const npi = practitioner.identifier?.find((id) => id.system === 'http://hl7.org/fhir/sid/us-npi')?.value ?? ''
  const email = practitioner.telecom?.find((t) => t.system === 'email')?.value ?? ''
  // roleTitle is config-driven: prefer the qualification coding matching the org's credential
  // system OID (coding.display, then that qualification's code.text); null when the OID is
  // configured but nothing matches; first qualification's code.text when the OID is absent.
  const roleTitle = roleTitleFromOid(practitioner, credentialSystemId) ?? ''
  // phone: prefer a work telecom, falling back to mobile (matches backend precedence).
  const phoneTelecom = practitioner.telecom?.filter((t) => t.system === 'phone') ?? []
  const phone =
    (
      phoneTelecom.find((t) => t.use === 'work')?.value ??
      phoneTelecom.find((t) => t.use === 'mobile')?.value ??
      phoneTelecom[0]?.value ??
      ''
    )?.trim() ?? ''
  // `Practitioner.id` is `string | undefined` in the FHIR type; fall back to the
  // request id so PhysicianInfo.id always satisfies its `string` contract.
  const id = practitioner.id ?? fallbackId

  return {
    authorizingProfessionalInfo: {
      npi,
      name: displayName,
      email: email || undefined,
    },
    patientPhysician: {
      id,
      name: { firstName, lastName },
      roleTitle,
      phone,
      email,
      npi,
    },
  }
}

/**
 * Read a single Practitioner by FHIR id via provider-api (POST /fhir/practitioner/read/{id}).
 * Returns null if the session is invalid, the id is empty, or the request fails.
 *
 * ``credentialSystemId`` is the org's configured FHIR credential identifier system OID
 * (``ui_config.providerCredentialSystemId``); when provided, roleTitle is resolved from the
 * matching ``Practitioner.qualification`` coding (see {@link roleTitleFromOid}).
 */
export async function readPractitionerById(
  epicState: string,
  practitionerId: string,
  signal?: AbortSignal,
  credentialSystemId?: string | null
): Promise<{ authorizingProfessionalInfo: AuthorizingProfessionalInfo; patientPhysician: PhysicianInfo } | null> {
  const client = EpicAuthClient.getInstance()
  if (!client.hasSession(epicState)) {
    return null
  }
  const accessToken = client.getAccessToken(epicState)
  const organizationId = client.getOrgId(epicState)
  if (!accessToken || !organizationId || !practitionerId.trim()) {
    return null
  }

  const id = encodeURIComponent(practitionerId.trim())
  const providerApi = ProviderAPIClient.getInstance()
  const response = await providerApi.post(
    `/fhir/practitioner/read/${id}`,
    { accessToken, organizationId },
    { 'Content-Type': 'application/json' },
    true,
    signal
  )

  if (!response.ok) {
    Sentry.captureException(new Error(`Practitioner read failed: ${response.status}`), {
      tags: { operation: 'practitioner.read', practitionerId, status: response.status },
    })
    return null
  }

  const pract = (await response.json()) as Practitioner
  return mapFhirPractitionerToAuthorizing(pract, practitionerId.trim(), credentialSystemId)
}

/**
 * Search practitioners by name via provider-api (proxied to Epic).
 * Requires Epic launch context (epicState) for accessToken and organizationId.
 *
 * `count` caps the number of practitioners returned (integer 1–100). Defaults to
 * {@link PRACTITIONER_SEARCH_COUNT}; pass `null` to omit it and use Epic's default page size.
 * Non-integer or out-of-range values are rejected server-side with HTTP 422, so the value is
 * clamped to an integer within 1–100 before sending.
 */
export async function searchPractitioners(
  epicState: string,
  name: string,
  signal?: AbortSignal,
  count: number | null = PRACTITIONER_SEARCH_COUNT
): Promise<{ authorizingProfessionalInfo: AuthorizingProfessionalInfo; patientPhysician: PhysicianInfo }[]> {
  const client = EpicAuthClient.getInstance()
  if (!client.hasSession(epicState)) {
    return []
  }
  const accessToken = client.getAccessToken(epicState)
  const organizationId = client.getOrgId(epicState)
  if (!accessToken || !organizationId) {
    return []
  }

  // Clamp to an integer within the server-accepted range (1–100) to avoid HTTP 422; `null`
  // means "omit `count`" so Epic's default page size is used.
  const normalizedCount =
    count === null || !Number.isFinite(count) ? null : Math.min(100, Math.max(1, Math.trunc(count)))

  const body: PractitionerSearchRequest = {
    accessToken,
    organizationId,
    name: name.trim() || undefined,
    ...(normalizedCount !== null ? { count: normalizedCount } : {}),
  }

  const providerApi = ProviderAPIClient.getInstance()
  const response = await providerApi.post(
    '/fhir/practitioner/search',
    body,
    { 'Content-Type': 'application/json' },
    true,
    signal
  )

  if (!response.ok) {
    Sentry.captureException(new Error(`Practitioner search failed: ${response.status}`), {
      tags: { operation: 'practitioner.search', status: response.status },
    })
    return []
  }

  const list = (await response.json()) as PractitionerSearchResponseItem[]
  return list.map(mapSearchItemToAuthorizing)
}
